//@ts-check
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

/**
 * @module Camera
 * @description This module provides functions and utilities for managing camera views and settings in a 3D scene using THREE.js.  
 */

/**
 * Sets pre named Views 
 * @param {string} view - Pass the view value in string format to set pre defined camera angle. Possible values are:
 * - "front"
 * - "back"
 * - "left"
 * - "right"
 * - "top"
 * - "bottom"
 * - "se"
 * - "sw"
 * - "ne"
 * - "nw"
 * @param {boolean} [animate=false] - Pass true value to animate the camera. By default it is false.
 * @returns {Object} Return success or error messages 
 * { status:  mesage: }
 */
export function setNamedCamera(view, animate = false) {
  try {
    const sphere = this.boundingSphere;
    const center = sphere.center.clone(); // Center of the bounding sphere
    const radius = sphere.radius;
    const distance = radius * 2.5; // Adjust distance as needed for framing
    view = view.toLowerCase();
    let cameraSet;
    let degree, axis, cameraPos, angleinRad, newPoints;

    //get perspectiveCamera aspect and fov for consistance aspect ratio
    const aspect = this.perspectiveCamera.aspect;
    const fov = this.perspectiveCamera.fov;
    const height = 2 * distance * Math.tan((fov * Math.PI) / 360);

    this.camera = new THREE.OrthographicCamera(
      (-aspect * height) / 2, // left
      (aspect * height) / 2,  // right
      height / 2,             // top
      -height / 2,            // bottom
      0.1,                     // near
      1000                     // far
    );

      switch (view) {
        case 'top':
        cameraSet = [0, 1, 0];
        this.setCameraPosition(center.x, center.y, (center.z + distance), cameraSet,  center, animate, view);
        break;
  
        case 'bottom':
          cameraSet = [0, 1, 0];
          this.setCameraPosition(center.x, center.y, center.z - distance, cameraSet,  center, animate, view);
          break;
  
        case 'front':
          cameraSet = [0, 0, 1];
          this.setCameraPosition(center.x, center.y - distance, center.z, cameraSet,  center, animate, view);
          break;
  
        case 'back':
          cameraSet = [0, 0, 1];
          this.setCameraPosition(center.x, center.y + distance, center.z, cameraSet,  center, animate, view);
          break;
  
        case 'left':
          cameraSet = [0, 0, 1];
          this.setCameraPosition(center.x - distance, center.y, center.z, cameraSet,  center, animate, view);
          break;
  
        case 'right':
          cameraSet = [0, 0, 1];
          this.setCameraPosition(center.x + distance, center.y, center.z, cameraSet,  center, animate, view);
          break;
  
        case 'se': // South-East view
          degree = 180;
          break;
    
        case 'sw': // South-West view
          degree = 90;
          break;
    
        case 'ne': // North-East view
          degree = 270;
          break;
    
        case 'nw': // North-West view
          degree = 0;
          break;
        default:
          throw new Error(`Unknown view: ${view}`);
      }
  
      if ((view === 'se') || (view === 'sw') || (view === 'ne') || (view === 'nw')){
        let x_distance = distance * 1.5 + center.x;  // Move eastward (X+)
        let y_distance = distance * (-1.5) + center.y;  // Move southward (Y-)
        let finalPos = new THREE.Vector3(x_distance, y_distance, center.z * 4).clone();
  
        axis = new THREE.Vector3(0, 0, 1);
        cameraPos = new THREE.Vector3().subVectors(finalPos, center); 
        angleinRad = (degree * Math.PI) / 180;
        cameraPos.applyAxisAngle(axis, angleinRad);
        newPoints = new THREE.Vector3();
        newPoints.addVectors(center, cameraPos);
        cameraSet = [0, 0, 1];
        this.setCameraPosition(newPoints.x, newPoints.y, newPoints.z, cameraSet, center, animate, view);
      }
      return {
        status: 'success',
        message: `Camera set to ${view} view.`,
      };
    } catch (err) {
      return {
        status: 'failed',
        message: `Failed to set the camera view: ${err.message}`,
      };
    }
  }

  /**
 * Sets camera position
 * @param {Number} x - x 
 * @param {Number} y - y
 * @param {Number} z - z
 * @param {Array.<Number>} cameraSet - coordinate system up
 * @param {Object} center 
 * @param {boolean} [animate=false]
 * @param {string} view - Pass the view value in string format to set pre defined camera angle. Possible values are:
 */
  export function setCameraPosition(x,y,z,cameraSet, center, animate, view){
   try {
    this.camera.position.set(x, y, z);
    this.camera.up.set(...cameraSet);
    this.camera.lookAt(center);
  
    // Set camera position and animate if necessary
    if (animate) {
      new TWEEN.Tween(this.camera.position)
        .to({ x: this.camera.position.x, y: this.camera.position.y, z: this.camera.position.z }, 2000)
        .easing(TWEEN.Easing.Sinusoidal.InOut)
        .start();
    } else {
      this.camera.updateProjectionMatrix();
      this.recalculateShadows();
      this.orbitControls = new OrbitControls(this.camera, this.labelRenderer.domElement);
      if ((view === 'top') || (view === 'bottom') || (view === 'front') || (view === 'back') ||(view === 'left') || (view === 'right')) {
        this.orbitControls.enableRotate = false;
      }else{
        this.orbitControls.enableRotate = true;
      }
      this.orbitControls.target.copy(center);
      this.orbitControls.update();
    }
    return {
      status: 'success',
      message: `Camera set to ${view} view.`,
    };
  } catch (err) {
    return {
      status: 'failed',
      message: `Failed to set the camera view: ${err.message}`,
    };
  }
}

/**
 * CustomCamera
 * @typedef {Object} customCamera
 * @property {string} type - Camera type: 
 * - "PerspectiveCamera"
 * - "OrthographicCamera"
 * @property {Number} near - Camera near
 * @property {Number} far - Camera far
 * @property {Number} fov -  Camera fov
 * @property {Array.<Number>} up 
 * @property {Number} zoom - zoom percentage
 * @property {Number} aspect - aspect
 * @property {Array.<Number>} position - Camera position
 * @property {Array.<Object>} rotation - Camera rotation
 * @property {Array.<Object>} target - Camera target
 */
/**
 * Sets the camera from the saved view
 * @param {customCamera} savedView 
 * @returns {Object} Return success or error messages 
 * { status:  mesage: }
 */
export function setCamera(savedView) {
  try {
    // Check if the saved view is provided
    if (!savedView) {
      console.error('View not found!');
      return {
        'status': 'failed',
        'message': 'No custom camera is provided'
      }
    }

    // Handle PerspectiveCamera setup
    if (savedView.type === 'PerspectiveCamera') {
      // Create a new PerspectiveCamera using saved view properties
      this.camera = new THREE.PerspectiveCamera(
        savedView.fov ? savedView.fov : 50,
        this.canvas.offsetWidth / this.canvas.offsetHeight,
        savedView.near,
        savedView.far
      );
      this.camera.up.set(savedView.up[0], savedView.up[1], savedView.up[2]);
      this.camera.zoom = savedView.zoom; // Restore zoom level
      // Initialize OrbitControls with rotation enabled
      this.orbitControls = new OrbitControls(this.camera, this.labelRenderer.domElement);
      this.orbitControls.enableRotate = true;
    }// Create a new OrthographicCamera using saved view properties
    else if (savedView.type === 'OrthographicCamera') {
      const sphere = this.boundingSphere; // Center of the bounding sphere
      const radius = sphere.radius;
      const distance = radius * 2.5;  // Adjust distance as needed for framing

      //get perspectiveCamera aspect and fov for consistance aspect ratio
      const aspect = this.perspectiveCamera.aspect;
      const fov = this.perspectiveCamera.fov;
      const height = 2 * distance * Math.tan((fov * Math.PI) / 360);

      this.camera = new THREE.OrthographicCamera(
        (-aspect * height) / 2,
        (aspect * height) / 2,
        height / 2,
        -height / 2,
        savedView.near,
        savedView.far
      );
      this.camera.up.set(savedView.up[0], savedView.up[1], savedView.up[2]);
      this.camera.zoom = savedView.zoom; // Restore zoom level
      // Initialize OrbitControls with rotation disabled
      this.orbitControls = new OrbitControls(this.camera, this.labelRenderer.domElement);
      this.orbitControls.enableRotate = false;
    }
  
    // Restore camera position and rotation from saved view
    this.camera.position.fromArray(savedView.position);
    this.camera.rotation.fromArray(savedView.rotation);

    // Restore orbit controls target position
    const target = new THREE.Vector3().fromArray(savedView.target);
    this.orbitControls.target.copy(target);
    this.orbitControls.update();

    // Ensure camera is looking at the target
    this.camera.lookAt(target);
    this.camera.updateProjectionMatrix();
    this.recalculateShadows();

    return {
      'status': 'succcess',
      'message': 'Successfully set the custom camera'
    }
  }
  catch (err) {
    const returnValue = {
      'status': 'failed',
      'message': 'Failed to set the custom camera view in 3D Viewer. ' + err.message
    }
    return returnValue;
  }
}


/**
 * Sets perspective camera
 */
export function setPerspectiveCamera() {
  this.camera = new THREE.PerspectiveCamera(50, this.canvas.offsetWidth / this.canvas.offsetHeight, this.near, 100000);
  this.camera.up.set(0, 0, 1);
  this.camera.lookAt(0, 0, 0);
  this.orbitControls = new OrbitControls(this.camera, this.labelRenderer.domElement);


  let percentage = 1;
  const sphere = this.boundingSphere;
  var r = sphere.radius * percentage,
    offset = r / Math.tan(Math.PI / 180.0 * this.orbitControls.object.fov * 0.5),
    vector = new THREE.Vector3(-1, -1, 1),
    dir = vector,
    newPos = new THREE.Vector3();
  dir.multiplyScalar(offset * 0.75);
  newPos.addVectors(sphere.center, dir);
  var target = new THREE.Vector3(sphere.center.x, sphere.center.y, sphere.center.z);

  this.camera.position.set(newPos.x, newPos.y, newPos.z);
  this.camera.lookAt(target);
  this.camera.updateProjectionMatrix();

  this.orbitControls.object.position.set(newPos.x, newPos.y, newPos.z);
  this.orbitControls.target = target;
  this.orbitControls.update();
}


/**
 * Sets centered camera 
 * @param {Number} elevation 
 * @param {Number} azimuth 
 * @param {Number} zoomFactor 
 * @param {boolean} [animate=false] - Pass true value to animate the camera. By default it is false.
 * @returns {Object} - Return success or error messages 
 * { status:  mesage: }
 */
export function setCenteredCamera(elevation, azimuth, zoomFactor, animate = false) {
  try {
    const currentViewer = this;
    if (currentViewer.arrayOfModel.length > 0) {
      for (let i = 0; i < currentViewer.arrayOfModel.length; i++) {
        const box = new THREE.Box3().setFromObject(currentViewer.arrayOfModel[i]);
        let vector = box.max.distanceTo(box.min);
        const center = box.getCenter(new THREE.Vector3());
        let y_distance = vector * 1.5 + center.y;
        const cameraInitialPosition = new THREE.Vector3(center.x, y_distance, center.z);
        const cameraPos = new THREE.Vector3().subVectors(cameraInitialPosition, center);

        //elevation angle calculation
        let angleinRadX = (elevation * Math.PI) / 180;
        let axisX = new THREE.Vector3(1, 0, 0);
        cameraPos.applyAxisAngle(axisX, angleinRadX);
        let newPointsX = new THREE.Vector3();
        newPointsX.addVectors(center, cameraPos);

        //azimuth angle calculation
        let angleinRadZ = (azimuth * Math.PI) / 180;
        let axisZ = new THREE.Vector3(0, 0, 1);
        cameraPos.applyAxisAngle(axisZ, angleinRadZ);
        let newPointsZ = new THREE.Vector3();
        newPointsZ.addVectors(center, cameraPos);

        currentViewer.recalculateShadows();
        currentViewer.render();
        if (currentViewer.animate || animate) {
          currentViewer.changeZoomFactorFromNewCameraCoordinates(zoomFactor, modelID, newPointsZ, true);
          currentViewer.render();
        }
        else {
          currentViewer.changeZoomFactorFromNewCameraCoordinates(zoomFactor, modelID, newPointsZ);
          currentViewer.render();
        }

        const returnValue = {
          'status': 'sucess',
          'message': 'Set the custom camera view in 3D Viewer. ' + err.message
        }
        return returnValue;
      }
    }
  }
  catch (err) {
    const returnValue = {
      'status': 'failed',
      'message': 'Failed to set the custom camera view in 3D Viewer. ' + err.message
    }
    return returnValue;
  }
}

/**
 * Gets current camera's coordinates
 * @returns {Object} - Current camera's position 
 */
export function getCameraCoordinates() {
  return { position: [this.camera.position.x, this.camera.position.y, this.camera.position.z] };
}


/**
 * Gets the current camera (entire object)
 * @returns {THREE.Camera} - Current Camera
 */
export function getCurrentCamera() {
  return this.camera;
}


/**
 * Retrieves specific attributes of the current camera
 * @returns {customCamera} 
 */
export function getCurrentCameraAttributes() {
  const cameraAttr = {
    type: this.camera.isPerspectiveCamera ? 'PerspectiveCamera' : 'OrthographicCamera',
    position: this.camera.position.toArray(),
    rotation: this.camera.rotation.toArray(),
    target: this.orbitControls.target.toArray(),
    zoom: this.camera.zoom,
    fov: this.camera.fov,
    aspect: this.camera.aspect,
    near: this.camera.near,
    far: this.camera.far,
    up: [this.camera.up.x, this.camera.up.y, this.camera.up.z]
  }
  return cameraAttr;
}

/**
 * Zoom to elements
 * @param {Array[]} element_Ids 
 * @returns {Object} Return success or error messages 
 */
export function zoomToElements(element_Ids) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0 && element_Ids.length >= 1) {

      var zoomGroup = new THREE.Group();
      for (let i = 0; i < element_Ids.length; i++) {
        let child = scope.elementsMap.get(element_Ids[i]);
        if (child instanceof THREE.Mesh) {
          zoomGroup.add(child.clone());
        }

      }
      scope.modelScene.add(zoomGroup);
      this.zoomTo(zoomGroup);
      scope.modelScene.remove(zoomGroup);

      var returnValue = {
        "status": "success",
        "message": "Zoom to elements API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": 'Failed to zoom to elements. ' + err.message
    }
    return returnValue;
  }
}


/**
 * Zoom to one single element 
 * Zoom to selection
 * @param {Number | String} element_ID 
 * @returns {Object} Return success or error messages
 */
export function zoomSelection(element_ID) {
  var scope = this;
  try {
    if (scope.modelScene.children.length != 0 && element_ID) {
      var zoomGroup = new THREE.Group();
      let child = scope.elementsMap.get(element_ID);
      if (child instanceof THREE.Mesh) {
        zoomGroup.add(child.clone());
      }
      scope.scene.add(zoomGroup);
      this.zoomTo(zoomGroup, 1.5, true);
      scope.scene.remove(zoomGroup);
      var returnValue = {
        "status": "success",
        "message": "Zoom selection API"
      }
      return returnValue;
    }
    else {
      var returnValue = {
        "status": "failed",
        "message": "ERROR : Model is not loaded in viewer."
      }
      return returnValue;
    }
  }
  catch (err) {
    var returnValue = {
      "status": "failed",
      "message": "Failed to zoom to selection " + err.message
    }
    return returnValue;
  }
}

// TODO this method should be here ??? Still needed ? 
/**
 * Sets the bounding box and the bounding sphere
 */
export function setBounds() {
  const scope = this;
  const object = scope.modelScene.children[0];
  this.boundingBox = new THREE.Box3().setFromObject(object);
  this.boundingSphere = this.boundingBox.getBoundingSphere(new THREE.Sphere());
}


/**
 * Zooms extents
 * @param {Number} [percentage = 1.5] - zoom percentage
 */
export function zoomExtents(percentage = 1.5) {
  const scope = this;
  const object = scope.modelScene.children[0];

  this.zoomTo(object, percentage);
}

/**
 * Zoom by percentage 
 * @param {Number} percentage 
 */
export function zoomByPercentage(percentage) {
  const scope = this;
  const object = scope.modelScene.children[0];

  this.zoomTo(object, percentage);
}

/**
 * Zoom to selected object
 * @param {THREE.Object3D} selection - selected three object 3d 
 * @param {Number} percentage - percentage
 */
export function zoomTo(selection, percentage) {
  const scope = this;
  var box = new THREE.Box3().setFromObject(selection);
  var sphere = box.getBoundingSphere(new THREE.Sphere());

  if (scope.camera instanceof THREE.PerspectiveCamera) {
    var r = sphere.radius * percentage,
      offset = r / Math.tan(Math.PI / 180.0 * scope.orbitControls.object.fov * 0.5),
      vector = new THREE.Vector3(0, 0, 1),
      dir = vector.applyQuaternion(scope.orbitControls.object.quaternion),
      newPos = new THREE.Vector3(),
      h = 0;
    dir.multiplyScalar(offset);
    var target = new THREE.Vector3(sphere.center.x, sphere.center.y, sphere.center.z - h);
    newPos.addVectors(sphere.center, dir);

    scope.camera.position.set(newPos.x, newPos.y, newPos.z - h);
    scope.camera.lookAt(target);
    scope.camera.updateProjectionMatrix();

    scope.orbitControls.object.position.set(newPos.x, newPos.y, newPos.z - h);
    scope.orbitControls.target = target;

    scope.orbitControls.update();
  } else {
    // Calculate camera direction vector
    const camDir = new THREE.Vector3();
    scope.camera.getWorldDirection(camDir);
    // Get the size and center of the bounding box
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    // Get perspective camera aspect ratio to maintain consistent scaling
    const aspectRatio = this.perspectiveCamera.aspect;

    let maxDim;
    // Determine the maximum dimension based on camera direction
    if (Math.abs(camDir.x) > 0.9) { // Side views (left/right)
      maxDim = Math.max(size.y / aspectRatio, size.z);
    } else if (Math.abs(camDir.y) > 0.9) {  // Front/Back views
      maxDim = Math.max(size.x / aspectRatio, size.z);
    } else { // Top/Bottom views
      maxDim = Math.max(size.x / aspectRatio, size.y);
    }
    // Calculate frustum size based on the maximum dimension and scaling percentage  
    const frustumSize = maxDim * percentage;
    scope.camera.zoom = 1;

    // Update the OrthographicCamera bounds
    scope.camera.left = -frustumSize * aspectRatio / 2;
    scope.camera.right = frustumSize * aspectRatio / 2;
    scope.camera.top = frustumSize / 2;
    scope.camera.bottom = -frustumSize / 2;
    scope.camera.updateProjectionMatrix();

    // Keep the orbit controls' target but avoid resetting the camera's position
    const offset = new THREE.Vector3().copy(scope.camera.position).sub(scope.orbitControls.target);
    scope.orbitControls.target.set(center.x, center.y, center.z);
    scope.camera.position.copy(center).add(offset);

    // Update controls to reflect the changes made to the camera
    scope.orbitControls.update();
  }
}